import {
  ActionIcon,
  Avatar,
  Button,
  FileButton,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { IconArrowDown, IconArrowUp, IconPhoto, IconTrash } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { EMPLOYMENT_TYPES, LINK_TYPES, PROFICIENCY } from "../../../shared/constants";
import type { MessageKey } from "../../../shared/i18n";
import type { ResumeDocument, ResumeProfile } from "../../api/client";
import { MonthInput } from "../../components/month-input";
import { useMessages } from "../../i18n/provider";
import styles from "./editor.module.css";

type EducationItem = ResumeDocument["education"][number];
type ExperienceItem = ResumeDocument["experience"][number];
type ProjectItem = ResumeDocument["projects"][number];
type LinkItem = ResumeDocument["links"][number];
type SkillItem = ResumeDocument["skills"][number];
type LanguageItem = ResumeDocument["languages"][number];
type CertificationItem = ResumeDocument["certifications"][number];
type AwardItem = ResumeDocument["awards"][number];

interface RowProps<T> {
  item: T;
  index: number;
  count: number;
  onPatch: (patch: Partial<T>) => void;
  onDelete: () => void;
  onMove: (direction: -1 | 1) => void;
}

const EMPLOYMENT_KEYS: Record<string, MessageKey> = {
  "full-time": "employment.fullTime",
  "part-time": "employment.partTime",
  contract: "employment.contract",
  internship: "employment.internship",
  freelance: "employment.freelance",
};

function useChoiceData() {
  const t = useMessages();
  return {
    linkTypes: LINK_TYPES.map((value) => ({
      value,
      label: t(`links.${value}` as MessageKey),
    })),
    employmentTypes: EMPLOYMENT_TYPES.map((value) => ({
      value,
      label: t(EMPLOYMENT_KEYS[value] ?? "experience.employmentType"),
    })),
    proficiency: PROFICIENCY.map((value) => ({
      value,
      label: t(`languages.${value}` as MessageKey),
    })),
  };
}

function ItemCard({
  title,
  meta,
  index,
  count,
  onMove,
  onDelete,
  children,
}: {
  title: string;
  meta?: string | null;
  index: number;
  count: number;
  onMove: (direction: -1 | 1) => void;
  onDelete: () => void;
  children: ReactNode;
}) {
  const t = useMessages();
  return (
    <div className={styles.item}>
      <div className={styles.itemHead}>
        <div className={styles.itemText}>
          <Text size="sm" fw={600} component="p" m={0}>
            {title || "—"}
          </Text>
          {meta ? (
            <Text size="xs" c="dimmed" component="p" m={0}>
              {meta}
            </Text>
          ) : null}
        </div>
        <Group gap={0}>
          <ActionIcon
            variant="subtle"
            color="gray"
            disabled={index === 0}
            onClick={() => onMove(-1)}
            aria-label={t("common.moveUp")}
          >
            <IconArrowUp size={15} stroke={1.5} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="gray"
            disabled={index === count - 1}
            onClick={() => onMove(1)}
            aria-label={t("common.moveDown")}
          >
            <IconArrowDown size={15} stroke={1.5} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="red"
            onClick={onDelete}
            aria-label={t("common.delete")}
          >
            <IconTrash size={15} stroke={1.5} />
          </ActionIcon>
        </Group>
      </div>
      <Stack gap="sm">{children}</Stack>
    </div>
  );
}

function highlightsText(items: string[]): string {
  return items.join("\n");
}

function parseLines(value: string): string[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function parseTags(value: string): string[] {
  return value
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
}

export function ProfileEditor({
  profile,
  onPatch,
  onUploadAvatar,
  uploading,
}: {
  profile: ResumeProfile;
  onPatch: (patch: Partial<ResumeProfile>) => void;
  onUploadAvatar: (file: File) => void;
  uploading: boolean;
}) {
  const t = useMessages();
  return (
    <Stack gap="md">
      <Group align="center" gap="md">
        <Avatar src={profile.avatarUrl ?? undefined} size={56} radius="xl">
          {(profile.fullName || "?").slice(0, 1).toUpperCase()}
        </Avatar>
        <div>
          <FileButton
            onChange={(file) => {
              if (file) onUploadAvatar(file);
            }}
            accept="image/jpeg,image/png,image/webp,image/avif"
          >
            {(props) => (
              <Button
                {...props}
                variant="light"
                size="xs"
                leftSection={<IconPhoto size={15} stroke={1.5} />}
                loading={uploading}
              >
                {t("profile.uploadPhoto")}
              </Button>
            )}
          </FileButton>
          <Text size="xs" c="dimmed" mt={6}>
            {t("errors.fileType")}
          </Text>
        </div>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <TextInput
          label={t("profile.fullName")}
          value={profile.fullName}
          maxLength={120}
          onChange={(event) => onPatch({ fullName: event.currentTarget.value })}
        />
        <TextInput
          label={t("profile.headline")}
          value={profile.headline ?? ""}
          maxLength={160}
          onChange={(event) => onPatch({ headline: event.currentTarget.value })}
        />
      </SimpleGrid>

      <Textarea
        label={t("profile.summary")}
        description={t("profile.summaryHint")}
        value={profile.summary ?? ""}
        maxLength={3000}
        autosize
        minRows={3}
        maxRows={8}
        onChange={(event) => onPatch({ summary: event.currentTarget.value })}
      />

      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput
            label={t("profile.email")}
            type="email"
            value={profile.email ?? ""}
            onChange={(event) => onPatch({ email: event.currentTarget.value })}
          />
          <TextInput
            label={t("profile.phone")}
            value={profile.phone ?? ""}
            onChange={(event) => onPatch({ phone: event.currentTarget.value })}
          />
          <TextInput
            label={t("profile.location")}
            value={profile.location ?? ""}
            onChange={(event) => onPatch({ location: event.currentTarget.value })}
          />
        </SimpleGrid>
        <Group gap="lg">
          <Switch
            label={t("profile.showEmail")}
            checked={profile.showEmail}
            onChange={(event) => onPatch({ showEmail: event.currentTarget.checked })}
          />
          <Switch
            label={t("profile.showPhone")}
            checked={profile.showPhone}
            onChange={(event) => onPatch({ showPhone: event.currentTarget.checked })}
          />
        </Group>
      </Stack>
    </Stack>
  );
}

export function ExperienceEditor(props: RowProps<ExperienceItem>) {
  const t = useMessages();
  const { item, onPatch } = props;
  const { employmentTypes } = useChoiceData();
  return (
    <ItemCard
      title={item.role || item.company}
      meta={item.company}
      index={props.index}
      count={props.count}
      onMove={props.onMove}
      onDelete={props.onDelete}
    >
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <TextInput
          label={t("experience.role")}
          value={item.role}
          onChange={(event) => onPatch({ role: event.currentTarget.value })}
        />
        <TextInput
          label={t("experience.company")}
          value={item.company}
          onChange={(event) => onPatch({ company: event.currentTarget.value })}
        />
        <MonthInput
          label={t("experience.startDate")}
          value={item.startDate ?? ""}
          onChange={(event) => onPatch({ startDate: event.currentTarget.value || null })}
        />
        <MonthInput
          label={t("experience.endDate")}
          value={item.endDate ?? ""}
          disabled={item.isCurrent}
          onChange={(event) => onPatch({ endDate: event.currentTarget.value || null })}
        />
      </SimpleGrid>
      <Switch
        label={t("experience.current")}
        checked={item.isCurrent}
        onChange={(event) =>
          onPatch({
            isCurrent: event.currentTarget.checked,
            endDate: event.currentTarget.checked ? null : item.endDate,
          })
        }
      />
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput
            label={t("experience.location")}
            value={item.location ?? ""}
            onChange={(event) => onPatch({ location: event.currentTarget.value })}
          />
          <Select
            label={t("experience.employmentType")}
            data={employmentTypes}
            value={item.employmentType}
            clearable
            onChange={(value) =>
              onPatch({
                employmentType: value as ExperienceItem["employmentType"],
              })
            }
          />
        </SimpleGrid>
        <Textarea
          label={t("experience.highlights")}
          description={t("experience.highlightPlaceholder")}
          value={highlightsText(item.highlights)}
          autosize
          minRows={3}
          onChange={(event) => onPatch({ highlights: parseLines(event.currentTarget.value) })}
        />
      </Stack>
    </ItemCard>
  );
}

export function EducationEditor(props: RowProps<EducationItem>) {
  const t = useMessages();
  const { item, onPatch } = props;
  return (
    <ItemCard
      title={item.institution}
      meta={[item.degree, item.fieldOfStudy].filter(Boolean).join(" · ")}
      index={props.index}
      count={props.count}
      onMove={props.onMove}
      onDelete={props.onDelete}
    >
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <TextInput
          label={t("education.institution")}
          value={item.institution}
          onChange={(event) => onPatch({ institution: event.currentTarget.value })}
        />
        <TextInput
          label={t("education.degree")}
          value={item.degree ?? ""}
          onChange={(event) => onPatch({ degree: event.currentTarget.value })}
        />
        <MonthInput
          label={t("education.startDate")}
          value={item.startDate ?? ""}
          onChange={(event) => onPatch({ startDate: event.currentTarget.value || null })}
        />
        <MonthInput
          label={t("education.endDate")}
          value={item.endDate ?? ""}
          disabled={item.isCurrent}
          onChange={(event) => onPatch({ endDate: event.currentTarget.value || null })}
        />
      </SimpleGrid>
      <Switch
        label={t("education.current")}
        checked={item.isCurrent}
        onChange={(event) =>
          onPatch({
            isCurrent: event.currentTarget.checked,
            endDate: event.currentTarget.checked ? null : item.endDate,
          })
        }
      />
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput
            label={t("education.fieldOfStudy")}
            value={item.fieldOfStudy ?? ""}
            onChange={(event) => onPatch({ fieldOfStudy: event.currentTarget.value })}
          />
          <TextInput
            label={t("education.location")}
            value={item.location ?? ""}
            onChange={(event) => onPatch({ location: event.currentTarget.value })}
          />
        </SimpleGrid>
        <Textarea
          label={t("education.description")}
          value={item.description ?? ""}
          autosize
          minRows={2}
          onChange={(event) => onPatch({ description: event.currentTarget.value })}
        />
      </Stack>
    </ItemCard>
  );
}

export function ProjectEditor(
  props: RowProps<ProjectItem> & { onUploadImage: (file: File) => void },
) {
  const t = useMessages();
  const { item, onPatch } = props;
  return (
    <ItemCard
      title={item.name}
      meta={item.role}
      index={props.index}
      count={props.count}
      onMove={props.onMove}
      onDelete={props.onDelete}
    >
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <TextInput
          label={t("projects.name")}
          value={item.name}
          onChange={(event) => onPatch({ name: event.currentTarget.value })}
        />
        <TextInput
          label={t("projects.url")}
          value={item.url ?? ""}
          onChange={(event) => onPatch({ url: event.currentTarget.value })}
        />
      </SimpleGrid>
      <Textarea
        label={t("projects.description")}
        value={item.description ?? ""}
        autosize
        minRows={2}
        onChange={(event) => onPatch({ description: event.currentTarget.value })}
      />
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <MonthInput
          label={t("projects.startDate")}
          value={item.startDate ?? ""}
          onChange={(event) => onPatch({ startDate: event.currentTarget.value || null })}
        />
        <MonthInput
          label={t("projects.endDate")}
          value={item.endDate ?? ""}
          disabled={item.isCurrent}
          onChange={(event) => onPatch({ endDate: event.currentTarget.value || null })}
        />
      </SimpleGrid>
      <Switch
        label={t("projects.current")}
        checked={item.isCurrent}
        onChange={(event) =>
          onPatch({
            isCurrent: event.currentTarget.checked,
            endDate: event.currentTarget.checked ? null : item.endDate,
          })
        }
      />
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput
            label={t("projects.role")}
            value={item.role ?? ""}
            onChange={(event) => onPatch({ role: event.currentTarget.value })}
          />
          <TextInput
            label={t("projects.repoUrl")}
            value={item.repoUrl ?? ""}
            onChange={(event) => onPatch({ repoUrl: event.currentTarget.value })}
          />
        </SimpleGrid>
        <TextInput
          label={t("projects.tech")}
          description={t("projects.techPlaceholder")}
          value={item.tech.join(", ")}
          onChange={(event) => onPatch({ tech: parseTags(event.currentTarget.value) })}
        />
        <Textarea
          label={t("projects.highlights")}
          value={highlightsText(item.highlights)}
          autosize
          minRows={2}
          onChange={(event) => onPatch({ highlights: parseLines(event.currentTarget.value) })}
        />
        <Switch
          label={t("projects.featured")}
          checked={item.featured}
          onChange={(event) => onPatch({ featured: event.currentTarget.checked })}
        />
        <Group gap="sm">
          <FileButton
            onChange={(file) => {
              if (file) props.onUploadImage(file);
            }}
            accept="image/jpeg,image/png,image/webp,image/avif"
          >
            {(buttonProps) => (
              <Button
                {...buttonProps}
                variant="light"
                size="xs"
                leftSection={<IconPhoto size={15} stroke={1.5} />}
              >
                {t("projects.uploadImage")}
              </Button>
            )}
          </FileButton>
          {item.imageUrl ? <Avatar src={item.imageUrl} radius="sm" size={36} /> : null}
        </Group>
      </Stack>
    </ItemCard>
  );
}

export function SkillEditor(props: RowProps<SkillItem>) {
  const t = useMessages();
  const { item, onPatch } = props;
  return (
    <ItemCard
      title={item.name}
      meta={item.category}
      index={props.index}
      count={props.count}
      onMove={props.onMove}
      onDelete={props.onDelete}
    >
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
        <TextInput
          label={t("skills.name")}
          value={item.name}
          onChange={(event) => onPatch({ name: event.currentTarget.value })}
        />
        <TextInput
          label={t("skills.category")}
          value={item.category ?? ""}
          onChange={(event) => onPatch({ category: event.currentTarget.value })}
        />
        <Select
          label={t("skills.level")}
          data={["1", "2", "3", "4", "5"]}
          value={item.level ? String(item.level) : null}
          clearable
          onChange={(value) => onPatch({ level: value ? Number(value) : null })}
        />
      </SimpleGrid>
    </ItemCard>
  );
}

export function LanguageEditor(props: RowProps<LanguageItem>) {
  const t = useMessages();
  const { item, onPatch } = props;
  const { proficiency } = useChoiceData();
  return (
    <ItemCard
      title={item.name}
      index={props.index}
      count={props.count}
      onMove={props.onMove}
      onDelete={props.onDelete}
    >
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <TextInput
          label={t("languages.name")}
          value={item.name}
          onChange={(event) => onPatch({ name: event.currentTarget.value })}
        />
        <Select
          label={t("languages.proficiency")}
          data={proficiency}
          value={item.proficiency}
          allowDeselect={false}
          onChange={(value) => onPatch({ proficiency: value as LanguageItem["proficiency"] })}
        />
      </SimpleGrid>
    </ItemCard>
  );
}

export function CertificationEditor(props: RowProps<CertificationItem>) {
  const t = useMessages();
  const { item, onPatch } = props;
  return (
    <ItemCard
      title={item.name}
      meta={item.issuer}
      index={props.index}
      count={props.count}
      onMove={props.onMove}
      onDelete={props.onDelete}
    >
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <TextInput
          label={t("certifications.name")}
          value={item.name}
          onChange={(event) => onPatch({ name: event.currentTarget.value })}
        />
        <TextInput
          label={t("certifications.issuer")}
          value={item.issuer ?? ""}
          onChange={(event) => onPatch({ issuer: event.currentTarget.value })}
        />
        <MonthInput
          label={t("certifications.issueDate")}
          value={item.issueDate ?? ""}
          onChange={(event) => onPatch({ issueDate: event.currentTarget.value || null })}
        />
        <MonthInput
          label={t("certifications.expiryDate")}
          value={item.expiryDate ?? ""}
          onChange={(event) => onPatch({ expiryDate: event.currentTarget.value || null })}
        />
      </SimpleGrid>
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput
            label={t("certifications.credentialId")}
            value={item.credentialId ?? ""}
            onChange={(event) => onPatch({ credentialId: event.currentTarget.value })}
          />
          <TextInput
            label={t("certifications.credentialUrl")}
            value={item.credentialUrl ?? ""}
            onChange={(event) => onPatch({ credentialUrl: event.currentTarget.value })}
          />
        </SimpleGrid>
      </Stack>
    </ItemCard>
  );
}

export function AwardEditor(props: RowProps<AwardItem>) {
  const t = useMessages();
  const { item, onPatch } = props;
  return (
    <ItemCard
      title={item.title}
      meta={item.issuer}
      index={props.index}
      count={props.count}
      onMove={props.onMove}
      onDelete={props.onDelete}
    >
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <TextInput
          label={t("awards.title")}
          value={item.title}
          onChange={(event) => onPatch({ title: event.currentTarget.value })}
        />
        <TextInput
          label={t("awards.issuer")}
          value={item.issuer ?? ""}
          onChange={(event) => onPatch({ issuer: event.currentTarget.value })}
        />
      </SimpleGrid>
      <MonthInput
        label={t("awards.date")}
        value={item.date ?? ""}
        onChange={(event) => onPatch({ date: event.currentTarget.value || null })}
      />
      <Textarea
        label={t("awards.description")}
        value={item.description ?? ""}
        autosize
        minRows={2}
        onChange={(event) => onPatch({ description: event.currentTarget.value })}
      />
    </ItemCard>
  );
}

export function LinkEditor(props: RowProps<LinkItem>) {
  const t = useMessages();
  const { item, onPatch } = props;
  const { linkTypes } = useChoiceData();
  return (
    <ItemCard
      title={item.label}
      meta={item.url}
      index={props.index}
      count={props.count}
      onMove={props.onMove}
      onDelete={props.onDelete}
    >
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
        <Select
          label={t("links.type")}
          data={linkTypes}
          value={item.type}
          allowDeselect={false}
          onChange={(value) => onPatch({ type: (value ?? "custom") as LinkItem["type"] })}
        />
        <TextInput
          label={t("links.label")}
          value={item.label}
          onChange={(event) => onPatch({ label: event.currentTarget.value })}
        />
        <TextInput
          label={t("links.url")}
          value={item.url}
          onChange={(event) => onPatch({ url: event.currentTarget.value })}
        />
      </SimpleGrid>
    </ItemCard>
  );
}
